/**
 * PaywallProductPickerControl — Inline expandable picker for paywall plan
 * products.
 *
 * Replaces the old MUI `<Select>` that lived inside `SubscriptionItemEditor`.
 * Lives INSIDE the section card (no portal, no modal). Trigger click expands
 * a panel containing tabs (Sandbox / Live), search, product list with type +
 * trial chips, mixed-mode warning, and an embedded "Create new product"
 * button that opens `PaywallProductDialog`.
 *
 * Layered milestones:
 *   M1: trigger + collapsible panel + back-compat testid (this file)
 *   M2: tabs + Stripe-only filter + lazy active-tab init
 *   M3: search + ProductOptionRow with chips + selection highlight
 *   M4: inline PaywallProductDialog mount + auto-select on save
 *   M5: paywallModeMismatch warning + loading/empty/error states
 *
 * Plan: .claude/plans/workflow-partitioned-feather.md
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Collapse,
  Tabs,
  Tab,
  TextField,
  Chip,
  InputAdornment,
  Menu,
  MenuItem,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CheckIcon from '@mui/icons-material/Check';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { Button, CircularProgress, Alert } from '@mui/material';
import type { PaywallProduct } from '@segmently/schema-models/payments';
import { PaywallProductDialog } from '@modules/paywall-products/components/PaywallProductDialog';
import { getStripeConnectService } from '@modules/stripe-connect/services/StripeConnectService';
import { setReturnUrl, redirectToOAuth } from '@modules/stripe-connect/utils/oauthReturnUrl';
import { EDITOR } from '@test-kit/selectors/editor';
import { detectModeMismatch, detectProviderMismatch } from '@modules/reactFlow/components/onboarding/editorSectionsV2/paywall/paywallModeMismatch';
import { PaddlePaywallProductDialog } from '@modules/paywall-products/components/PaddlePaywallProductDialog';
import type { PaddlePickerConnectionProps } from '@modules/reactFlow/components/onboarding/editorSectionsV2/paywall/usePaddlePickerConnection';
import { ConnectProviderBanner } from '@modules/reactFlow/components/onboarding/editorSectionsV2/paywall/ConnectProviderBanner';
import { ConfirmBeforeConnectDialog } from './ConfirmBeforeConnectDialog';
import { productTabOf, providerOf, environmentLabelOf, PROVIDER_LABEL, type PickerTab, type PaywallProvider } from '@modules/reactFlow/components/onboarding/editorSectionsV2/paywall/productTabOf';

// ============================================================================
// TYPES
// ============================================================================

/**
 * Tab modes. Mirrors PaywallProduct.mode but kept as a local string union so
 * we don't leak schema-model types into the component's surface area.
 */
export type { PickerTab } from './productTabOf';

/**
 * Props for PaywallProductPickerControl.
 *
 * Note that `useStripeConnect` is NOT called from within this component —
 * the hook is lifted to V2PaywallSubscriptionsSection so a single status
 * subscription covers all plan rows. The derived state arrives here as
 * `sandboxConnected` / `liveConnected` (the data state) plus reason
 * strings.
 *
 * M7 change: the previous `liveTabEnabled` prop is split into the data
 * state `liveConnected`. The tab itself is NEVER disabled — when its
 * mode is not connected, the panel content swaps to a Connect CTA
 * banner instead of disabling the tab.
 *
 * Likewise `usePaywallProducts` lives in the parent; we receive the
 * provider-aware list (`allProducts`), loading/error/refresh signals,
 * and a `createProduct` callback bound to the parent's hook instance.
 */
export interface PaywallProductPickerControlProps extends PaddlePickerConnectionProps {
  /** 0-based plan row index — suffixes every internal testid. */
  rowIndex: number;
  /** Currently-selected productId from `items[i].productId`. Empty string means "no selection". */
  currentProductId: string;
  /**
   * Full PaywallProduct list. Paddle environment maps to Sandbox/Live;
   * Stripe retains its existing mode and connection gates.
   */
  allProducts: PaywallProduct[];
  /**
   * Product IDs from `items[k].productId` for every k !== rowIndex on the
   * same paywall. Used to detect mode-mismatch warnings (M5).
   */
  otherProductIds: string[];
  /**
   * Whether the Sandbox Stripe account is connected and healthy. Parent
   * derives from `useStripeConnect.testStatus`. When false, clicking the
   * Sandbox tab shows a Connect CTA banner in the panel content area.
   */
  sandboxConnected: boolean;
  /**
   * Display-only reason string explaining why the Sandbox account isn't
   * connected ("Checking Stripe connection…", "Connect a Sandbox Stripe
   * account first in Settings → Integrations", etc.). Null when connected.
   */
  sandboxNotConnectedReason: string | null;
  /**
   * Whether the Live Stripe account is connected and healthy. Parent
   * derives from `useStripeConnect.liveStatus`. When false, clicking the
   * Live tab shows a Connect CTA banner in the panel content area.
   */
  liveConnected: boolean;
  /** Display-only reason for unconnected Live state. Null when connected. */
  liveNotConnectedReason: string | null;
  /** Whether `usePaywallProducts` is in its loading phase. */
  productsLoading: boolean;
  /** Stripe account reads are still pending; absence is not an empty catalog. */
  stripeConnectionLoading?: boolean;
  /** Error message from `usePaywallProducts`, or null. */
  productsError: string | null;
  /** Retry callback bound to `usePaywallProducts.refresh`. */
  onRetryProducts: () => Promise<void>;
  /**
   * Selection callback. Receives the chosen productId (or empty string to
   * clear). Parent writes this to `content.paywall.products.items.{i}.productId`.
   */
  onSelect: (productId: string) => void;
  /**
   * Create-product callback. Picker calls this from its dialog `onSave`
   * wrapper; the parent forwards to `usePaywallProducts.createProduct(data)`.
   * Returns the created record so the picker can auto-select it.
   */
  onCreateProduct: (
    data: Omit<PaywallProduct, 'id' | 'createdAt' | 'updatedAt'>,
  ) => Promise<PaywallProduct>;
  /**
   * M7: optional save handler from the V2 screen editor dialog. When
   * provided, clicking Connect opens a save-guard modal. When omitted,
   * Connect skips the modal and goes straight to the OAuth redirect.
   */
  onSaveScreen?: () => Promise<void>;
  /**
   * M7: whether the editor has unsaved changes. Drives the save-guard
   * modal's button set (3 buttons when dirty, 2 when clean). Ignored
   * when `onSaveScreen` is not provided.
   */
  hasUnsavedChanges?: boolean;
  /**
   * M7: optional step ID for the screen being edited. Stashed in
   * sessionStorage so the post-OAuth handler can reopen the editor.
   */
  stepId?: string;
  /** Forwarded for localized strings (M3+). */
  locale: string;
  /** Passed THROUGH to PaywallProductDialog (the dialog needs projectId). */
  projectId: string;
  /**
   * Stable editor-session key used to preserve open/closed state across parent
   * remounts. Embed-child ProductCatalog editors can remount while async Stripe
   * state resolves; without this key the inline panel collapses mid-selection.
   */
  persistenceKey?: string;
  /** When true, the picker renders as disabled — trigger cannot be clicked. */
  disabled?: boolean;
}

// ============================================================================
// HELPERS
// ============================================================================

/**
 * Compute the initial active tab from the currently-selected product so a
 * live-mode selection doesn't visually "disappear" when the picker first
 * opens on a default Sandbox tab.
 *
 * Falls back to `'test'` when there is no selection or the product cannot
 * be resolved yet (e.g. products still loading).
 */
function resolveInitialTab(
  currentProductId: string,
  allProducts: PaywallProduct[],
): PickerTab {
  if (!currentProductId) return 'test';
  const product = allProducts.find((p) => p.id === currentProductId);
  if (!product) return 'test';
  return productTabOf(product) ?? 'test';
}

const expandedStateByPersistenceKey = new Map<string, boolean>();

/**
 * Resolve the chip text for a product's type.
 * Stripe semantics: `'one_time'` → one-shot Charge; `'subscription'` → Recurring.
 */
function getTypeChipLabel(product: PaywallProduct): string {
  return product.type === 'subscription' ? 'Subscription' : 'Purchase';
}

/**
 * Resolve the trial chip text for a product.
 *
 * Precedence (paid-trial wins when both are set, matching Stripe semantics
 * where `payment_behavior: 'allow_incomplete'` paid-trial replaces the free
 * trial period — paid is always the more material commitment for the user):
 *   1. `subscription.paidTrial.enabled === true` → "Paid Trial"
 *   2. `subscription.trialDays > 0`             → "Free Trial Nd"
 *   3. neither                                   → null (no trial chip)
 */
function getTrialChipLabel(product: PaywallProduct): string | null {
  if (product.type !== 'subscription') return null;
  if (product.subscription?.paidTrial?.enabled) return 'Paid Trial';
  const days = product.subscription?.trialDays ?? 0;
  if (days > 0) return `Free Trial ${days}d`;
  return null;
}

/**
 * Single product row in the picker list. Clickable; shows check-mark when
 * selected.
 */
interface ProductOptionRowProps {
  rowIndex: number;
  product: PaywallProduct;
  isSelected: boolean;
  onClick: () => void;
}

const ProductOptionRow: React.FC<ProductOptionRowProps> = React.memo(
  ({ rowIndex, product, isSelected, onClick }) => {
    const trialLabel = getTrialChipLabel(product);
    const displayPrice = extractDisplayPrice(product);

    return (
      <Box
        component="button"
        type="button"
        onClick={onClick}
        data-testid={EDITOR.paywallProductPicker.option(rowIndex, product.id)}
        // Stable, name-keyed handle so E2E can target an option deterministically
        // by the product NAME (which the test knows) without resolving productId
        // or relying on free-text matching. See EDITOR.paywallProductPicker.optionByName.
        data-product-name={product.name}
        aria-selected={isSelected}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          width: '100%',
          px: 1,
          py: 0.75,
          mb: 0.25,
          border: '1px solid',
          borderColor: isSelected ? 'primary.main' : 'transparent',
          bgcolor: isSelected ? 'action.selected' : 'transparent',
          borderRadius: 1,
          cursor: 'pointer',
          font: 'inherit',
          textAlign: 'left',
          '&:hover': {
            bgcolor: isSelected ? 'action.selected' : 'action.hover',
          },
          '&:focus-visible': {
            outline: '2px solid',
            outlineColor: 'primary.main',
            outlineOffset: 1,
          },
        }}
      >
        {/* Check icon column — fixed width so rows align when not selected. */}
        <Box sx={{ width: 20, display: 'flex', justifyContent: 'center' }}>
          {isSelected && <CheckIcon fontSize="small" color="primary" />}
        </Box>

        {/* Name + price column — flex-1, truncates long names. */}
        <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minWidth: 0 }}>
          <Typography variant="body2" noWrap>
            {product.name}
          </Typography>
          {displayPrice && (
            <Typography variant="caption" color="text.secondary" noWrap>
              {displayPrice}
            </Typography>
          )}
        </Box>

        {/* Provider, type and optional trial. */}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'flex-end', gap: 0.5, maxWidth: '55%' }}>
          <Chip label={PROVIDER_LABEL[providerOf(product)]} size="small" variant="outlined"
            data-testid={EDITOR.paywallProductPicker.providerChip(rowIndex, product.id)}
            sx={{ height: 20, '& .MuiChip-label': { px: 0.75, fontSize: '0.7rem' } }} />
          <Chip
            label={getTypeChipLabel(product)}
            size="small"
            variant="outlined"
            data-testid={EDITOR.paywallProductPicker.typeChip(rowIndex, product.id)}
            sx={{ height: 20, '& .MuiChip-label': { px: 0.75, fontSize: (theme) => theme.typography.caption.fontSize } }}
          />
          {trialLabel && (
            <Chip
              label={trialLabel}
              size="small"
              color="warning"
              variant="outlined"
              data-testid={EDITOR.paywallProductPicker.trialChip(rowIndex, product.id)}
              sx={{ height: 20, '& .MuiChip-label': { px: 0.75, fontSize: (theme) => theme.typography.caption.fontSize } }}
            />
          )}
        </Box>
      </Box>
    );
  },
);
ProductOptionRow.displayName = 'ProductOptionRow';

// ============================================================================
// COMPONENT
// ============================================================================

const PaywallProductPickerControlComponent: React.FC<
  PaywallProductPickerControlProps
> = ({
  rowIndex,
  currentProductId,
  allProducts,
  disabled = false,
  paddleSandboxConnected = false,
  paddleProductionConnected = false,
  paddleSandboxNotConnectedReason = null,
  paddleProductionNotConnectedReason = null,
  sandboxConnected,
  sandboxNotConnectedReason,
  liveConnected,
  liveNotConnectedReason,
  onSelect,
  onCreateProduct,
  onSaveScreen,
  hasUnsavedChanges = false,
  stepId,
  projectId,
  otherProductIds,
  productsLoading,
  stripeConnectionLoading = false,
  productsError,
  onRetryProducts,
  locale: _locale,
  persistenceKey,
}) => {
  const triggerRef = useRef<HTMLButtonElement>(null);
  const restorePaddleFocusAfterSave = useRef(false);
  const pickerPersistenceKey = useMemo(
    () => persistenceKey ?? `${stepId ?? projectId}:paywall-product-picker:${rowIndex}`,
    [persistenceKey, projectId, rowIndex, stepId],
  );
  const [expanded, setExpandedState] = useState(
    () => expandedStateByPersistenceKey.get(pickerPersistenceKey) ?? false,
  );
  const setExpanded = useCallback(
    (next: React.SetStateAction<boolean>) => {
      setExpandedState((prev) => {
        const value = typeof next === 'function'
          ? (next as (prevState: boolean) => boolean)(prev)
          : next;
        expandedStateByPersistenceKey.set(pickerPersistenceKey, value);
        return value;
      });
    },
    [pickerPersistenceKey],
  );
  const [chosenTab, setActiveTab] = useState<PickerTab | null>(null);
  // Resolve late-loaded selections until the owner explicitly chooses a tab.
  const activeTab = chosenTab ?? resolveInitialTab(currentProductId, allProducts);
  const [searchValue, setSearchValue] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [createProvider, setCreateProvider] = useState<PaywallProvider>('stripe');
  useEffect(() => {
    if (dialogOpen || !restorePaddleFocusAfterSave.current) return;
    // The nested Paddle Dialog restores its opener during unmount. That opener
    // disappears when the picker closes, so restore the row after modal cleanup.
    const frame = requestAnimationFrame(() => {
      restorePaddleFocusAfterSave.current = false;
      triggerRef.current?.focus();
    });
    return () => cancelAnimationFrame(frame);
  }, [dialogOpen]);
  const [createMenuAnchor, setCreateMenuAnchor] = useState<HTMLElement | null>(null);
  const [providerFilter, setProviderFilter] = useState<'all' | PaywallProvider>('all');
  // M7: save-guard modal state. `connectDialogOpen` controls visibility;
  // `pendingConnectMode` remembers which Stripe mode the user clicked so
  // we can pass it down after the dialog resolves.
  const [connectDialogOpen, setConnectDialogOpen] = useState(false);
  const [pendingConnectMode, setPendingConnectMode] = useState<PickerTab | null>(null);

  useEffect(() => {
    setExpandedState(expandedStateByPersistenceKey.get(pickerPersistenceKey) ?? false);
  }, [pickerPersistenceKey]);

  /**
   * Catalog visibility is not Paddle connection readiness. Existing valid Paddle
   * products remain browsable; publish and checkout validate the connection.
   * Stripe catalog/creation retain their own connection gates.
   */
  const activeStripeConnected = activeTab === 'test' ? sandboxConnected : liveConnected;
  // Existing Paddle catalog remains selectable without requiring a Stripe connection.
  // Publish/checkout validate the actual Paddle connection server-side.
  const activeTabHasPaddleProducts = allProducts.some(p => p.paymentProvider === 'paddle'
    && productTabOf(p) === activeTab);
  const activePaddleConnected = activeTab === 'test' ? paddleSandboxConnected : paddleProductionConnected;
  const paddleReason = activeTab === 'test' ? paddleSandboxNotConnectedReason : paddleProductionNotConnectedReason;
  const canBrowseActiveCatalog = activeStripeConnected || activePaddleConnected || activeTabHasPaddleProducts || stripeConnectionLoading;
  const hasBothProviders = allProducts.some(p => providerOf(p) === 'stripe' && productTabOf(p))
    && allProducts.some(p => providerOf(p) === 'paddle' && productTabOf(p));
  // A stale, hidden filter must not suppress the only provider left in the catalog.
  const visibleProvider = hasBothProviders ? providerFilter : 'all';
  const preferredProvider: PaywallProvider = allProducts.some(p => providerOf(p) === 'paddle'
    && (p.id === currentProductId || otherProductIds.includes(p.id))) ? 'paddle' : 'stripe';
  const activeTabNotConnectedReason =
    activeTab === 'test' ? sandboxNotConnectedReason : liveNotConnectedReason;

  // Resolve the current product for trigger label display
  const currentProduct = useMemo(
    () => allProducts.find((p) => p.id === currentProductId) ?? null,
    [allProducts, currentProductId],
  );

  // Products filtered by (a) the active tab's mode AND (b) case-insensitive
  // substring search on product.name. Empty/whitespace search reveals all
  // products of the active mode.
  const filteredProducts = useMemo(() => {
    const trimmedSearch = searchValue.trim().toLowerCase();
    return allProducts.filter((p) => {
      const mode = productTabOf(p);
      if (visibleProvider !== 'all' && providerOf(p) !== visibleProvider) return false;
      if (mode !== activeTab || (p.paymentProvider !== 'paddle' && !activeStripeConnected)) return false;
      if (!trimmedSearch) return true;
      return p.name.toLowerCase().includes(trimmedSearch);
    });
  }, [allProducts, activeTab, searchValue, activeStripeConnected, visibleProvider]);

  const waitingForStripeCatalog = stripeConnectionLoading && visibleProvider !== 'paddle' && filteredProducts.length === 0;

  // Stripe mode handling stays unchanged; Paddle environment/provider conflicts block publication.
  const mismatch = useMemo(
    () => detectModeMismatch(currentProductId, otherProductIds, allProducts),
    [currentProductId, otherProductIds, allProducts],
  );

  const providerMismatch = detectProviderMismatch(currentProductId, otherProductIds, allProducts);

  const handleTriggerClick = useCallback(() => {
    if (disabled) return;
    setExpanded((prev) => !prev);
  }, [disabled]);

  const handleTabChange = useCallback(
    (_event: React.SyntheticEvent, newTab: PickerTab) => {
      // M7: both tabs are always clickable. When the mode isn't connected,
      // the panel content swaps to the Connect CTA banner instead of
      // disabling the tab itself.
      setActiveTab(newTab);
      // Reset search on tab change so the new mode's list is unfiltered (AC-005).
      setSearchValue('');
    },
    [],
  );

  const handleSearchChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setSearchValue(event.target.value);
    },
    [],
  );

  /**
   * Row click handler. Three cases:
   *   1. Clicking the already-selected product → no onSelect (idempotent per
   *      AC-EC-6), but still collapse the picker.
   *   2. Clicking a different product → write productId via onSelect AND
   *      collapse the picker (so the user sees the new selection on the
   *      trigger immediately).
   *   3. Disabled state → blocked at the trigger level so we never reach here.
   */
  const handleSelectProduct = useCallback(
    (productId: string) => {
      if (productId !== currentProductId) {
        onSelect(productId);
      }
      setExpanded(false);
      // The selected option leaves the tab order when Collapse closes.
      // Keep keyboard users at the originating row, including idempotent selection.
      triggerRef.current?.focus();
    },
    [currentProductId, onSelect],
  );

  const handleOpenCreateDialog = useCallback((event: React.MouseEvent<HTMLElement>) => {
    if (activeStripeConnected && activePaddleConnected) {
      setCreateMenuAnchor(event.currentTarget);
    } else if (activeStripeConnected || activePaddleConnected) {
      setCreateProvider(activePaddleConnected ? 'paddle' : 'stripe');
      setDialogOpen(true);
    }
  }, [activeStripeConnected, activePaddleConnected]);
  const handleCloseCreateDialog = useCallback(() => setDialogOpen(false), []);

  /**
   * M7: trigger the actual OAuth redirect for the given mode. Writes the
   * return URL to sessionStorage so the global StripeOAuthReturnHandler
   * can bounce the user back to the editor when they return.
   *
   * This is intentionally fire-and-forget — `window.location.href` is a
   * synchronous navigation, the rest of this component will unmount.
   */
  const triggerOAuthRedirect = useCallback(
    async (mode: PickerTab) => {
      try {
        const service = getStripeConnectService();
        const oauthUrl = await service.getOAuthUrl(projectId, mode);
        // Stash return URL BEFORE redirect so it's available on return.
        setReturnUrl({
          url: window.location.pathname,
          mode,
          stepId,
          rowIndex,
        });
        // `redirectToOAuth` wraps `window.location.assign(url)` in a
        // standalone module-level function specifically so tests can mock
        // it. jsdom locks BOTH `location.href` (non-configurable setter)
        // AND `location.assign` (read-only method) against direct stubbing
        // — wrapping behind an importable function is the cleanest escape
        // hatch. Behavior in real browsers is full-page navigation.
        redirectToOAuth(oauthUrl);
      } catch (err) {
        // If the OAuth URL fetch fails (network, auth, etc.), surface
        // via console — the picker stays in its current state and the
        // user can retry by clicking Connect again. We deliberately do
        // NOT call setReturnUrl on failure.
        // eslint-disable-next-line no-console
        console.error('[PaywallProductPickerControl] OAuth URL fetch failed:', err);
      }
    },
    [projectId, stepId, rowIndex],
  );

  /**
   * M7: click handler for the Connect CTA in the banner. Three branches:
   *
   *   1. `onSaveScreen` not provided → skip the modal, redirect immediately.
   *      Defensive fallback for picker rendered without editor context.
   *   2. `onSaveScreen` provided + unsaved changes → open the 3-button
   *      save-guard modal.
   *   3. `onSaveScreen` provided + no unsaved changes → open the 2-button
   *      "Continue and connect" modal (still ask for confirmation so the
   *      redirect isn't surprising).
   */
  const handleConnectClick = useCallback(() => {
    if (disabled) return;
    const mode = activeTab;
    setPendingConnectMode(mode);
    if (!onSaveScreen) {
      // No save context — go straight to redirect.
      void triggerOAuthRedirect(mode);
      return;
    }
    setConnectDialogOpen(true);
  }, [disabled, activeTab, onSaveScreen, triggerOAuthRedirect]);

  /**
   * "Save and connect" handler passed into ConfirmBeforeConnectDialog.
   * The dialog awaits this; if it resolves, the dialog calls onProceed
   * which fires our handleConnectProceed below.
   */
  const handleSaveAndConnect = useCallback(async () => {
    if (!onSaveScreen) {
      // Shouldn't reach here — the modal only shows save buttons when
      // onSaveScreen is set. Defensive guard against a future regression.
      return;
    }
    await onSaveScreen();
  }, [onSaveScreen]);

  /** Dialog `onProceed` (success path for both Save+Connect and Discard+Connect). */
  const handleConnectProceed = useCallback(() => {
    const mode = pendingConnectMode;
    setConnectDialogOpen(false);
    setPendingConnectMode(null);
    if (mode) {
      void triggerOAuthRedirect(mode);
    }
  }, [pendingConnectMode, triggerOAuthRedirect]);

  const handleConnectCancel = useCallback(() => {
    setConnectDialogOpen(false);
    setPendingConnectMode(null);
  }, []);

  /**
   * Dialog save wrapper. Critical contract: `onSave` is `Promise<void>` —
   * if `onCreateProduct` rejects, this promise also rejects, and the dialog's
   * internal try/catch surfaces the error in its own UI (we do not need to
   * render our own error here).
   *
   * On the success path the order matters:
   *   1. `await onCreateProduct(data)` returns the created PaywallProduct
   *   2. `onSelect(created.id)` writes the new productId to the schema
   *   3. Close the dialog
   *   4. Auto-close the picker so the user sees the new selection on the trigger
   *
   * Steps 2-4 must run AFTER the await resolves successfully; if the create
   * fails, the dialog stays open with the form data preserved (AC-019).
   */
  const handleDialogSave = useCallback(
    async (data: Omit<PaywallProduct, 'id' | 'createdAt' | 'updatedAt'>) => {
      const created = await onCreateProduct(data);
      onSelect(created.id);
      setActiveTab(productTabOf(created));
      if (createProvider === 'paddle') restorePaddleFocusAfterSave.current = true;
      setDialogOpen(false);
      setExpanded(false);
      if (createProvider !== 'paddle') triggerRef.current?.focus();
    },
    [onCreateProduct, onSelect, createProvider],
  );

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 0.5,
      }}
    >
      {/* Trigger — preserves legacy testid for back-compat with existing E2E helpers. */}
      <Box
        component="button"
        type="button"
        onClick={handleTriggerClick}
        ref={triggerRef}
        disabled={disabled}
        data-testid={EDITOR.paywallProductPicker.trigger(rowIndex)}
        aria-expanded={expanded}
        aria-haspopup="listbox"
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
          minHeight: 40,
          px: 1.5,
          py: 0.5,
          bgcolor: 'background.paper',
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1,
          cursor: disabled ? 'not-allowed' : 'pointer',
          opacity: disabled ? 0.5 : 1,
          font: 'inherit',
          textAlign: 'left',
          width: '100%',
          '&:hover': {
            borderColor: disabled ? 'divider' : 'action.hover',
          },
          '&:focus-visible': {
            outline: '2px solid',
            outlineColor: 'primary.main',
            outlineOffset: 1,
          },
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minWidth: 0 }}>
          {currentProduct ? (
            <>
              <Typography variant="body2" noWrap>
                {currentProduct.name}
              </Typography>
              <Typography variant="caption" color="text.secondary" noWrap>
                {PROVIDER_LABEL[providerOf(currentProduct)]}{environmentLabelOf(currentProduct) ? ` · ${environmentLabelOf(currentProduct)}` : ''}
              </Typography>
              {currentProduct.preBakedPrice?.localized && (
                <Typography variant="caption" color="text.secondary" noWrap>
                  {extractDisplayPrice(currentProduct)}
                </Typography>
              )}
            </>
          ) : (
            <Typography variant="body2" color="text.secondary" component="em">
              Select a product
            </Typography>
          )}
        </Box>
        <IconButton
          component="span"
          size="small"
          disabled={disabled}
          sx={{
            transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 150ms ease',
            pointerEvents: 'none',
          }}
        >
          <ExpandMoreIcon fontSize="small" />
        </IconButton>
      </Box>

      {/* Expandable panel — populated by M2-M5. */}
      <Collapse in={expanded} unmountOnExit>
        <Box
          data-testid={EDITOR.paywallProductPicker.panel(rowIndex)}
          sx={{
            mt: 0.5,
            bgcolor: 'background.paper',
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            maxHeight: 560,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {/* M2: Tabs row — Sandbox is always enabled; Live is gated by parent. */}
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            variant="fullWidth"
            sx={{
              borderBottom: '1px solid',
              borderColor: 'divider',
              minHeight: 36,
              '& .MuiTab-root': { minHeight: 36, textTransform: 'none' },
            }}
          >
            {/*
              M7: both tabs are always clickable now. When a tab's mode is
              not connected, the panel content swaps to a Connect CTA
              banner (see below). The previous "disabled Live tab + tooltip"
              pattern is retired in favor of clearer content-area messaging.
            */}
            <Tab
              value="test"
              label="Sandbox"
              data-testid={EDITOR.paywallProductPicker.tabSandbox(rowIndex)}
            />
            <Tab
              value="live"
              label={visibleProvider === 'paddle' ? 'Production' : 'Live'}
              data-testid={EDITOR.paywallProductPicker.tabLive(rowIndex)}
            />
          </Tabs>

          {hasBothProviders && (
            <Box role="group" aria-label="Payment provider" sx={{ p: 1, display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
              {(['all', 'stripe', 'paddle'] as const).map(provider => (
                <Chip key={provider} label={provider === 'all' ? 'All providers' : PROVIDER_LABEL[provider]} size="small"
                  variant={visibleProvider === provider ? 'filled' : 'outlined'} aria-pressed={visibleProvider === provider}
                  data-testid={EDITOR.paywallProductPicker.providerFilter(rowIndex, provider)}
                  onClick={() => setProviderFilter(provider)} />
              ))}
            </Box>
          )}
          {providerMismatch.mismatch && <Alert severity="warning" data-testid={EDITOR.paywallProductPicker.providerWarning(rowIndex)} sx={{ borderRadius: 0 }}>
            Stripe and Paddle products are mixed. Publishing is blocked until every plan uses one provider.
          </Alert>}
          {mismatch.mismatch && (
            <Alert
              severity="warning"
              icon={<WarningAmberIcon fontSize="small" />}
              data-testid={EDITOR.paywallProductPicker.mixedModeWarning(rowIndex)}
              sx={{
                borderRadius: 0,
                borderBottom: '1px solid',
                borderColor: 'divider',
                py: 0.5,
                '& .MuiAlert-message': { fontSize: '0.75rem' },
              }}
            >
              {currentProduct && providerOf(currentProduct) === 'paddle'
                ? 'Publishing is blocked until every plan uses one environment'
                : 'Mixed modes detected — only the mode of the first Stripe product on this paywall will be used at publish time.'}
            </Alert>
          )}

          {!canBrowseActiveCatalog ? (
            <ConnectProviderBanner
              rowIndex={rowIndex}
              mode={activeTab}
              projectId={projectId}
              stripeReason={activeTabNotConnectedReason}
              paddleReason={paddleReason}
              preferredProvider={preferredProvider}
              onConnectStripe={handleConnectClick}
              disabled={disabled}
            />
          ) : (
            <>
          {activeTabHasPaddleProducts && !activePaddleConnected && visibleProvider !== 'stripe' && (
            <Alert severity="info" data-testid={EDITOR.paywallProductPicker.paddleStatus(rowIndex)} sx={{ borderRadius: 0 }}>
              {paddleReason ?? 'Check the Paddle connection before publishing.'}
              <Button component="a" href={`/project/${encodeURIComponent(projectId)}/integrations?open=paddle`} target="_blank" rel="noopener noreferrer"
                size="small" data-testid={EDITOR.paywallProductPicker.connectPaddle(rowIndex)} disabled={!projectId}>Open Paddle settings</Button>
            </Alert>
          )}
          {/* Search input — case-insensitive substring match on product.name. */}
          <Box sx={{ p: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search products"
              value={searchValue}
              onChange={handleSearchChange}
              disabled={productsLoading || !!productsError}
              inputProps={{
                'data-testid': EDITOR.paywallProductPicker.search(rowIndex),
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          {/*
            Product list — three mutually-exclusive states:
              (1) loading → centered spinner (distinct from empty)
              (2) error   → ErrorOutline + retry button (distinct from empty;
                             prevents users from misreading a backend failure
                             as "no products configured" per shared-test-
                             navigation Rule #14)
              (3) empty   → friendly create-first message
              (4) default → ProductOptionRow list with chips + selection
          */}
          <Box sx={{ overflow: 'auto', p: 1, flex: 1 }}>
            {productsLoading || waitingForStripeCatalog ? (
              <Box
                data-testid={EDITOR.paywallProductPicker.loadingSpinner(rowIndex)}
                sx={{ display: 'flex', justifyContent: 'center', py: 3 }}
              >
                <CircularProgress size={24} />
              </Box>
            ) : productsError ? (
              <Box
                data-testid={EDITOR.paywallProductPicker.errorState(rowIndex)}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 1,
                  py: 2,
                  px: 1,
                  textAlign: 'center',
                }}
              >
                <ErrorOutlineIcon color="error" fontSize="small" />
                <Typography variant="caption" color="error">
                  {productsError}
                </Typography>
                <Button
                  size="small"
                  variant="text"
                  onClick={() => {
                    void onRetryProducts();
                  }}
                  data-testid={EDITOR.paywallProductPicker.retryBtn(rowIndex)}
                  sx={{ textTransform: 'none' }}
                >
                  Retry
                </Button>
              </Box>
            ) : filteredProducts.length === 0 ? (
              <Typography
                variant="caption"
                color="text.secondary"
                data-testid={EDITOR.paywallProductPicker.emptyState(rowIndex)}
                sx={{ display: 'block', p: 1, textAlign: 'center' }}
              >
                {searchValue
                  ? `No products match "${searchValue}".`
                  : `No ${activeTab === 'test' ? 'sandbox' : 'live'} products yet — Create one.`}
              </Typography>
            ) : (
              filteredProducts.map((product) => (
                <ProductOptionRow
                  key={product.id}
                  rowIndex={rowIndex}
                  product={product}
                  isSelected={product.id === currentProductId}
                  onClick={() => handleSelectProduct(product.id)}
                />
              ))
            )}
          </Box>

          {/* Footer — "Create new product" opens PaywallProductDialog inline. */}
          <Box
            sx={{
              p: 1,
              borderTop: '1px solid',
              borderColor: 'divider',
              bgcolor: 'background.default',
            }}
          >
            <Button fullWidth variant={filteredProducts.length === 0 ? 'contained' : 'outlined'} size="small"
              startIcon={<AddIcon />} disabled={disabled || (!activeStripeConnected && !activePaddleConnected)}
              onClick={handleOpenCreateDialog} data-testid={EDITOR.paywallProductPicker.createNewBtn(rowIndex)}
              aria-haspopup={activeStripeConnected && activePaddleConnected ? 'menu' : 'dialog'}
              sx={{ textTransform: 'none' }}>
              Create new product
            </Button>
            <Menu anchorEl={createMenuAnchor} open={Boolean(createMenuAnchor)} onClose={() => setCreateMenuAnchor(null)}
              data-testid={EDITOR.paywallProductPicker.createNewMenu(rowIndex)}>
              <MenuItem disabled={!activeStripeConnected} data-testid={EDITOR.paywallProductPicker.createNewMenuStripe(rowIndex)}
                onClick={() => { setCreateMenuAnchor(null); setCreateProvider('stripe'); setDialogOpen(true); }}>Stripe</MenuItem>
              <MenuItem disabled={!activePaddleConnected} data-testid={EDITOR.paywallProductPicker.createNewMenuPaddle(rowIndex)}
                onClick={() => { setCreateMenuAnchor(null); setCreateProvider('paddle'); setDialogOpen(true); }}>Paddle</MenuItem>
            </Menu>
          </Box>
            </>
          )}
        </Box>
      </Collapse>

      {/*
        PaywallProductDialog mounted inline. Uses MUI <Dialog> which portals
        to document.body, so the dialog visually overlays the editor regardless
        of where it's mounted — that's intentional (full-screen modal feel)
        and the picker stays open behind it. On dialog Save success the wrapper
        in `handleDialogSave` auto-selects + auto-closes the picker.

        `defaultMode={activeTab}` ensures the new product starts in the same
        mode as the tab the user was on (AC-015 / AC-016).
      */}
      {dialogOpen && createProvider === 'stripe' && (
        <PaywallProductDialog
          open={dialogOpen}
          product={null}
          projectId={projectId}
          defaultMode={activeTab}
          onClose={handleCloseCreateDialog}
          onSave={handleDialogSave}
        />
      )}

      {dialogOpen && createProvider === 'paddle' && <PaddlePaywallProductDialog
        open product={null} projectId={projectId} defaultEnvironment={activeTab === 'test' ? 'sandbox' : 'production'}
        onClose={handleCloseCreateDialog} onSave={handleDialogSave} />}

      {/*
        M7: save-guard modal triggered by the Connect CTA banner. Only
        mounts when an editor save handler is wired in — otherwise the
        Connect click skips this dialog and redirects immediately.
        `pendingConnectMode` carries the user's choice through the modal.
      */}
      {connectDialogOpen && pendingConnectMode && (
        <ConfirmBeforeConnectDialog
          open={connectDialogOpen}
          mode={pendingConnectMode}
          hasUnsavedChanges={hasUnsavedChanges}
          onSaveAndConnect={handleSaveAndConnect}
          onProceed={handleConnectProceed}
          onCancel={handleConnectCancel}
        />
      )}
    </Box>
  );
};

/**
 * Extract a display price string from a PaywallProduct's localized prices.
 * Picks the first locale entry (matches the previous picker's behavior).
 *
 * M3 will replace this with locale-aware lookup.
 */
function extractDisplayPrice(product: PaywallProduct): string {
  const localized = product.preBakedPrice?.localized;
  if (!localized) return '';
  const locales = Object.keys(localized);
  if (locales.length === 0) return '';
  return localized[locales[0]]?.displayPrice ?? '';
}

export const PaywallProductPickerControl = React.memo(
  PaywallProductPickerControlComponent,
);
PaywallProductPickerControl.displayName = 'PaywallProductPickerControl';
